export type DashboardColumnKey =
	| 'id'
	| 'subject'
	| 'requester'
	| 'contact'
	| 'assignee'
	| 'status'
	| 'priority'
	| 'channel'
	| 'project'
	| 'tags'
	| 'created'
	| 'sla_state'
	| 'sla_due_at'
	| 'sla_breached_at'
	| 'sla_paused_at'
	| 'resolution_time'
	| 'first_response_time'
	| 'last_activity_at'
	| 'resolved_at'
	| 'time_in_current_status'
	| 'sla_policy_name'
	| 'watchers_count';

export interface DashboardColumnDef {
	key: DashboardColumnKey;
	label: string;
	width?: string;
	toggleable: boolean;
	defaultVisible: boolean;
	resolvedViewDefault?: boolean;
}

export const DASHBOARD_COLUMNS: readonly DashboardColumnDef[] = [
	{key: 'id', label: 'ID', width: '100px', toggleable: false, defaultVisible: true},
	{key: 'subject', label: 'Subject', toggleable: false, defaultVisible: true},
	{key: 'requester', label: 'Requester', width: '190px', toggleable: true, defaultVisible: true},
	{key: 'contact', label: 'Contact', width: '175px', toggleable: true, defaultVisible: false},
	{key: 'assignee', label: 'Assignee', width: '175px', toggleable: true, defaultVisible: true},
	{key: 'status', label: 'Status', width: '175px', toggleable: true, defaultVisible: true},
	{key: 'priority', label: 'Priority', width: '120px', toggleable: true, defaultVisible: true},
	{key: 'channel', label: 'Channel', width: '130px', toggleable: true, defaultVisible: true},
	{key: 'project', label: 'Project', width: '200px', toggleable: true, defaultVisible: true},
	// Tags and resolution time are resolved-view defaults, not base defaults — the
	// base set is the PRD's 10 (§8.1 narrative). Marking either `defaultVisible`
	// would make 11 columns visible on the default view.
	{key: 'tags', label: 'Tags', width: '200px', toggleable: true, defaultVisible: false, resolvedViewDefault: true},
	{key: 'created', label: 'Created', width: '120px', toggleable: true, defaultVisible: true},
	{key: 'sla_state', label: 'SLA', width: '150px', toggleable: true, defaultVisible: true},
	{key: 'sla_due_at', label: 'SLA due', width: '130px', toggleable: true, defaultVisible: false},
	{key: 'sla_breached_at', label: 'SLA breached', width: '150px', toggleable: true, defaultVisible: false},
	{key: 'sla_paused_at', label: 'SLA paused', width: '150px', toggleable: true, defaultVisible: false},
	{
		key: 'resolution_time',
		label: 'Resolution time',
		width: '155px',
		toggleable: true,
		defaultVisible: false,
		resolvedViewDefault: true,
	},
	{key: 'first_response_time', label: 'First response', width: '150px', toggleable: true, defaultVisible: false},
	{key: 'last_activity_at', label: 'Last activity', width: '130px', toggleable: true, defaultVisible: false},
	{key: 'resolved_at', label: 'Resolved', width: '120px', toggleable: true, defaultVisible: false},
	{key: 'time_in_current_status', label: 'Time in status', width: '150px', toggleable: true, defaultVisible: false},
	{key: 'sla_policy_name', label: 'SLA policy', width: '150px', toggleable: true, defaultVisible: false},
	{key: 'watchers_count', label: 'Watchers', width: '110px', toggleable: true, defaultVisible: false},
];

export const DASHBOARD_COLUMN_WIDTHS: Readonly<Record<DashboardColumnKey, string | undefined>> = DASHBOARD_COLUMNS.reduce(
	(acc, col) => ({...acc, [col.key]: col.width}),
	{} as Record<DashboardColumnKey, string | undefined>
);

export const DASHBOARD_COLUMN_LABELS: Readonly<Record<DashboardColumnKey, string>> = DASHBOARD_COLUMNS.reduce(
	(acc, col) => ({...acc, [col.key]: col.label}),
	{} as Record<DashboardColumnKey, string>
);

export const DASHBOARD_TOGGLEABLE_COLUMNS: readonly DashboardColumnDef[] = DASHBOARD_COLUMNS.filter(c => c.toggleable);

export const DASHBOARD_ALWAYS_ON_COLUMN_KEYS: ReadonlySet<DashboardColumnKey> = new Set(
	DASHBOARD_COLUMNS.filter(c => !c.toggleable).map(c => c.key)
);

export const DASHBOARD_BASE_DEFAULT_VISIBLE: ReadonlySet<DashboardColumnKey> = new Set(
	DASHBOARD_COLUMNS.filter(c => c.defaultVisible).map(c => c.key)
);

export const DASHBOARD_RESOLVED_VIEW_EXTRA_DEFAULTS: ReadonlySet<DashboardColumnKey> = new Set(
	DASHBOARD_COLUMNS.filter(c => c.resolvedViewDefault && !c.defaultVisible).map(c => c.key)
);
